import { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "@models/auth/IAuthModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IStorageModel, SketchBlobKind } from "@models/storage/IStorageModel";
import type { ISketchModel } from "./ISketchModel";

/**
 * `SketchModel` — web port of Unity
 * `Models/AnnotationsModel/SketchModel.cs` (image pipeline only; see the
 * interface header for the ownership split and deliberate deviations).
 *
 * Cache shape mirrors Unity's three per-id `Texture2D` dictionaries
 * (`m_backgroundTextureCache` / `m_textureCache` / `m_previewCache`) as three
 * per-id `blob:`-URL maps with per-id in-flight promise dedup (the web
 * equivalent of the `AsyncNamedLock` quick fix for C3D-2800).
 */
export class SketchModel implements ISketchModel {
  readonly onScreenshotUpdated = new Emitter<{ sketchId: string }>();
  readonly onSketchUploaded = new Emitter<{ sketchId: string }>();
  readonly onEditingCancelled = new Emitter<{ sketchId: string }>();

  private m_editingSketchId: string | null = null;
  private readonly m_urlCache = new Map<string, string | null>();
  private readonly m_inFlight = new Map<string, Promise<string | null>>();

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_auth: IAuthModel,
    private readonly m_storage: IStorageModel,
  ) {
    // Unity clears the caches on scene teardown (AnnotationsLayerModel
    // OnDestroy); the web clears per project close/switch.
    this.m_project.onActiveProjectChanged.on(() => this.clearAllCaches());
  }

  get editingSketchId(): string | null {
    return this.m_editingSketchId;
  }

  beginEdit(sketchId: string): void {
    this.m_editingSketchId = sketchId;
  }

  endEdit(): void {
    this.m_editingSketchId = null;
  }

  cancelSketch(): void {
    const sketchId = this.m_editingSketchId;
    this.m_editingSketchId = null;
    if (sketchId) this.onEditingCancelled.emit({ sketchId });
  }

  loadBackgroundTexture(sketchId: string): Promise<string | null> {
    return this.loadLayer(sketchId, "background");
  }

  loadSketchTexture(sketchId: string): Promise<string | null> {
    return this.loadLayer(sketchId, "sketch");
  }

  loadSketchPreviewTexture(sketchId: string): Promise<string | null> {
    return this.loadLayer(sketchId, "preview");
  }

  async saveBackground(sketchId: string, pngBytes: Uint8Array): Promise<void> {
    this.setCached(sketchId, "background", bytesToBlobUrl(pngBytes));
    const customerId = this.customerId();
    if (!customerId) return;
    await this.m_storage.putSketchObject(customerId, sketchId, "background", pngBytes);
  }

  async updateScreenshot(
    sketchId: string,
    strokesPng: Uint8Array,
    previewPng: Uint8Array,
  ): Promise<void> {
    // Caches update + `onScreenshotUpdated` fire BEFORE the uploads finish —
    // Unity's SaveSketch raises ScreenshotUpdated (closing the editor) and
    // lets the S3 puts continue in the background.
    this.setCached(sketchId, "sketch", bytesToBlobUrl(strokesPng));
    this.setCached(sketchId, "preview", bytesToBlobUrl(previewPng));
    this.onScreenshotUpdated.emit({ sketchId });

    const customerId = this.customerId();
    if (customerId) {
      // Unity upload order: "-preview.png" then ".png" (SketchModel.cs:475).
      await this.m_storage.putSketchObject(customerId, sketchId, "preview", previewPng);
      await this.m_storage.putSketchObject(customerId, sketchId, "sketch", strokesPng);
    }
    this.onSketchUploaded.emit({ sketchId });
  }

  deleteCachedTextures(sketchId: string): void {
    for (const kind of ["sketch", "background", "preview"] as const) {
      const key = cacheKey(sketchId, kind);
      const url = this.m_urlCache.get(key);
      if (url) URL.revokeObjectURL(url);
      this.m_urlCache.delete(key);
      this.m_inFlight.delete(key);
    }
  }

  applyRemoteSketchUploaded(sketchId: string): void {
    // Peer finished (re-)uploading this sketch's PNGs: drop stale local
    // copies and let renderers re-fetch (Unity DownloadSketchTexture).
    // Emits onScreenshotUpdated — NOT onSketchUploaded, which is reserved
    // for LOCAL saves (the registration layer republishes it as the
    // `sketch_uploaded` event; re-emitting it here would ping-pong the
    // event between clients forever).
    this.deleteCachedTextures(sketchId);
    this.onScreenshotUpdated.emit({ sketchId });
  }

  // -- Internals ---------------------------------------------------

  private loadLayer(sketchId: string, kind: SketchBlobKind): Promise<string | null> {
    const key = cacheKey(sketchId, kind);
    const cached = this.m_urlCache.get(key);
    if (cached !== undefined) return Promise.resolve(cached);
    const inFlight = this.m_inFlight.get(key);
    if (inFlight) return inFlight;

    const customerId = this.customerId();
    if (!customerId) return Promise.resolve(null);

    const load = this.m_storage
      .getSketchBlobUrl(customerId, sketchId, kind)
      .then((url) => {
        // A save/purge that raced this fetch wins — don't clobber it.
        if (this.m_inFlight.get(key) === load) {
          this.m_inFlight.delete(key);
          if (!this.m_urlCache.has(key)) this.m_urlCache.set(key, url);
          else if (url) URL.revokeObjectURL(url);
        } else if (url) {
          URL.revokeObjectURL(url);
        }
        return this.m_urlCache.get(key) ?? null;
      })
      .catch((err) => {
        this.m_inFlight.delete(key);
        console.warn(`[SketchModel] load ${kind} failed for ${sketchId}:`, err);
        return null;
      });
    this.m_inFlight.set(key, load);
    return load;
  }

  private setCached(sketchId: string, kind: SketchBlobKind, url: string): void {
    const key = cacheKey(sketchId, kind);
    const prev = this.m_urlCache.get(key);
    if (prev) URL.revokeObjectURL(prev);
    this.m_inFlight.delete(key);
    this.m_urlCache.set(key, url);
  }

  private clearAllCaches(): void {
    for (const url of this.m_urlCache.values()) {
      if (url) URL.revokeObjectURL(url);
    }
    this.m_urlCache.clear();
    this.m_inFlight.clear();
    this.m_editingSketchId = null;
  }

  private customerId(): string | null {
    if (!this.m_storage.isOnline) return null;
    return this.m_auth.currentUser?.customerId ?? null;
  }
}

function cacheKey(sketchId: string, kind: SketchBlobKind): string {
  return `${sketchId}|${kind}`;
}

function bytesToBlobUrl(bytes: Uint8Array): string {
  return URL.createObjectURL(new Blob([bytes as BlobPart], { type: "image/png" }));
}
